import { Activity, AlertTriangle, CheckCircle2, Loader2, PackageOpen, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { fetchActiveSubpacks } from '@/api/admin';
import AdminDateRange, { dateRangeParams, EMPTY_DATE_RANGE, useDebouncedValue } from '@/components/admin/AdminDateRange';
import AdminPagination from '@/components/admin/AdminPagination';
import { fmtUsd } from '@/lib/money';

/**
 * Active Subpacks Oversight — read-only admin subpage.
 *
 * UX contract:
 *   - Two stat cards on top:
 *       1) Active Subpacks Now  (live count of is_active=True)
 *       2) Total Completed      (naturally-completed, refunds excluded)
 *   - Paginated list (50 / page, server-side) of every active Sub Pack,
 *     newest first. Columns: Username | Subpack | Positions Released |
 *     Progress | Started.
 *   - Completed Sub Packs automatically drop off the list because the
 *     backend filter is is_active=True.
 *   - Manual "Refresh" button for when the operator wants a fresh pull
 *     without waiting for the auto-tick.
 *
 * Guardrail: auto-refresh is locked at 60 SECONDS (60000 ms) to match
 * the v6/v7 polling slowdown. DO NOT lower this — it was the root cause
 * of the v5-era self-inflicted DDoS alerts. The setInterval below is the
 * single source of truth for this cadence on this page.
 */
const ACTIVE_SUBPACKS_REFRESH_MS = 60_000; // 60s — DO NOT LOWER

// Brand accent per tier — re-uses the same color ladder as the Subpack
// Settings page so the UI feels cohesive.
const TIER_ACCENTS = {
  5: '#FFE08A',
  10: '#FFD45E',
  25: '#FFC20E',
  50: '#FFAD00',
  100: '#FF9500',
  250: '#FF7A00',
  500: '#FF5C00',
};

const fmtRelative = (iso) => {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '—';
  const diffMs = Date.now() - t;
  if (diffMs < 0) return 'just now';
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  const yrs = Math.floor(months / 12);
  return `${yrs}y ago`;
};

// Progress bar — pure presentational, no third-party dep needed.
// Color stays in the brand gold ladder so it doesn't clash with the
// rest of the admin chrome.
const ProgressBar = ({ pct, accent }) => {
  const clamped = Math.max(0, Math.min(100, Number(pct) || 0));
  return (
    <div className="flex items-center gap-2 min-w-[140px]">
      <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,182,18,0.10)' }}>
        <div
          className="h-full rounded-full transition-[width] duration-500 ease-out"
          style={{
            width: `${clamped}%`,
            background: `linear-gradient(90deg, ${accent}cc, ${accent})`,
          }}
        />
      </div>
      <span className="text-[11px] font-medium sc-tabular text-[var(--sc-text-dim)] w-[42px] text-right" data-testid="active-subpack-progress-pct">
        {clamped.toFixed(1)}%
      </span>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, accent, testId }) => (
  <div className="sc-card flex items-center gap-4 p-5" data-testid={testId}>
    <div
      className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
      style={{
        background: `${accent}1f`,
        border: `1px solid ${accent}55`,
        color: accent,
      }}
    >
      <Icon className="w-5 h-5" strokeWidth={2.2} />
    </div>
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] font-medium">{label}</div>
      <div className="font-display font-bold text-2xl sc-tabular text-[var(--sc-text)] leading-tight mt-0.5" data-testid={`${testId}-value`}>
        {value}
      </div>
    </div>
  </div>
);

const SkeletonRow = () => (
  <div
    className="grid grid-cols-12 gap-3 items-center py-3 px-3 rounded-xl border border-[rgba(255,182,18,0.10)] bg-[rgba(0,0,0,0.30)]"
    data-testid="active-subpacks-skeleton-row"
  >
    <div className="col-span-3 h-3 rounded bg-[rgba(255,182,18,0.10)]" />
    <div className="col-span-2 h-3 rounded bg-[rgba(255,182,18,0.10)]" />
    <div className="col-span-2 h-3 rounded bg-[rgba(255,182,18,0.10)]" />
    <div className="col-span-3 h-3 rounded bg-[rgba(255,182,18,0.10)]" />
    <div className="col-span-2 h-3 rounded bg-[rgba(255,182,18,0.10)]" />
  </div>
);

const ActiveSubpacksSubpage = () => {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  // Purchase-date filter (server-side); changing it returns to page 1.
  const [dates, setDates] = useState(EMPTY_DATE_RANGE);
  const appliedDates = useDebouncedValue(dates);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [errored, setErrored] = useState(false);

  // Track the most recent page request so out-of-order responses
  // (e.g. user pages quickly while a slow request is in flight) can't
  // overwrite newer state.
  const reqIdRef = useRef(0);
  // Stable ref to whether we've already loaded once — lets us pick the
  // big skeleton vs. the small spinner without making ``data`` a
  // dependency of the load helper.
  const hasLoadedRef = useRef(false);

  // ``load`` is wrapped in useCallback with no deps: it only reads refs,
  // state setters and the imported fetcher, so its identity is stable and
  // ``page`` stays the only value that retriggers the effect below. It is
  // only ever invoked from inside async wrappers (the effect's IIFE, the
  // setInterval callback, the manual button handler).
  const load = useCallback(async (targetPage, targetDates, { silent } = {}) => {
    const reqId = ++reqIdRef.current;
    if (!silent) {
      if (!hasLoadedRef.current) setLoading(true);
      else setRefreshing(true);
    }
    try {
      const d = await fetchActiveSubpacks(targetPage, dateRangeParams(targetDates));
      if (reqId !== reqIdRef.current) return; // stale
      setData(d);
      setErrored(false);
      hasLoadedRef.current = true;
    } catch {
      if (reqId !== reqIdRef.current) return;
      setErrored(true);
      if (!silent) toast.error('Failed to load active subpacks');
    } finally {
      if (reqId === reqIdRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, []);

  // Initial fetch + auto-refresh tick. Strictly 60s — see file header.
  // Uses the project's standard async-IIFE-inside-effect pattern so the
  // linter sees no setState reachable synchronously from the effect body.
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!alive) return;
      await load(page, appliedDates);
    })();
    const id = setInterval(() => {
      if (!alive) return;
      (async () => {
        if (!alive) return;
        await load(page, appliedDates, { silent: true });
      })();
    }, ACTIVE_SUBPACKS_REFRESH_MS);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [load, page, appliedDates]);

  const onManualRefresh = () => {
    // Fire-and-forget; load() handles its own state + error toasts.
    void load(page, appliedDates);
  };

  const onPageChange = (next) => {
    if (next === page) return;
    setPage(next);
  };

  // ---- Render branches ----------------------------------------------

  if (loading && !data) {
    return (
      <div className="space-y-6" data-testid="active-subpacks-loading">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sc-card h-[92px] animate-pulse" />
          <div className="sc-card h-[92px] animate-pulse" />
        </div>
        <div className="sc-card space-y-2">
          {[...Array(6)].map((_, i) => (
            <SkeletonRow key={i} />
          ))}
        </div>
      </div>
    );
  }

  if (errored && !data) {
    return (
      <div className="sc-card flex flex-col items-center justify-center py-12 gap-3" data-testid="active-subpacks-error">
        <AlertTriangle className="w-6 h-6 text-[#FF7A00]" />
        <div className="text-sm text-[var(--sc-text-dim)]">Could not load active subpacks.</div>
        <button
          type="button"
          onClick={onManualRefresh}
          className="sc-btn-gold"
          style={{ padding: '8px 16px', fontSize: 12 }}
          data-testid="active-subpacks-retry"
        >
          Retry
        </button>
      </div>
    );
  }

  const items = data?.items || [];
  const activeCount = Number(data?.active_count ?? 0);
  const completedCount = Number(data?.completed_count ?? 0);
  const totalPages = Number(data?.total_pages ?? 1);
  const currentPage = Number(data?.page ?? page);

  return (
    <div className="space-y-6" data-testid="active-subpacks-page">
      {/* Stat strip ----------------------------------------------------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <StatCard icon={Activity} label="Active Subpacks Now" value={activeCount.toLocaleString()} accent="#FFC20E" testId="active-subpacks-stat-active" />
        <StatCard
          icon={CheckCircle2}
          label="Total Subpacks Completed"
          value={completedCount.toLocaleString()}
          accent="#5EE6A1"
          testId="active-subpacks-stat-completed"
        />
      </div>

      {/* Header bar with refresh control ------------------------------- */}
      <div className="sc-card">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div className="flex items-center gap-2">
            <PackageOpen className="w-4 h-4 text-[#FFC20E]" />
            <span className="text-[11px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">Live drip roster</span>
            <span className="sc-pill ml-1" style={{ padding: '2px 8px', fontSize: 9 }} title="Page auto-refreshes every 60 seconds">
              <span className="sc-dot" />
              auto · 60s
            </span>
          </div>
          <button
            type="button"
            onClick={onManualRefresh}
            disabled={refreshing}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[rgba(255,182,18,0.32)] text-[12px] text-[var(--sc-text-dim)] hover:border-[#FFC20E] hover:text-[#FFE08A] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="active-subpacks-refresh-btn"
          >
            {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Refresh
          </button>
        </div>
        <div className="mb-4 flex justify-end">
          <AdminDateRange
            wrap
            dateFrom={dates.date_from}
            dateTo={dates.date_to}
            onChange={(range) => {
              setDates(range);
              setPage(1);
            }}
            fromLabel="Purchased From"
            toLabel="Purchased To"
            testIdPrefix="active-subpacks-date"
          />
        </div>

        {/* Empty state ------------------------------------------------ */}
        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-14 gap-3 text-center" data-testid="active-subpacks-empty">
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center"
              style={{
                background: 'rgba(255,194,14,0.08)',
                border: '1px solid rgba(255,194,14,0.28)',
              }}
            >
              <PackageOpen className="w-6 h-6 text-[#FFC20E]" />
            </div>
            <div className="font-display text-base text-[#FFE08A] font-semibold">No active subpacks right now</div>
            <div className="text-[12px] text-[var(--sc-text-mute)] max-w-sm">
              When a user purchases a Sub Pack, it will appear here and automatically drop off once all positions have released.
            </div>
          </div>
        ) : (
          <>
            {/* Table -------------------------------------------------- */}
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]" data-testid="active-subpacks-table">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] border-b border-[rgba(255,182,18,0.10)]">
                    <th className="py-2 pr-3 font-medium">Username</th>
                    <th className="py-2 pr-3 font-medium">Subpack</th>
                    <th className="py-2 pr-3 font-medium">Positions</th>
                    <th className="py-2 pr-3 font-medium">Progress</th>
                    <th className="py-2 pr-3 font-medium text-right">Started</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => {
                    const accent = TIER_ACCENTS[row.tier_price_usd] || '#FFC20E';
                    const label = `${fmtUsd(row.tier_price_usd)} Pack`;
                    return (
                      <tr
                        key={row.pack_id}
                        className="border-b border-[rgba(255,182,18,0.06)] hover:bg-[rgba(255,182,18,0.03)] transition-colors"
                        data-testid={`active-subpack-row-${row.pack_id}`}
                      >
                        <td className="py-3 pr-3">
                          <span className="text-[var(--sc-text)] font-medium" data-testid="active-subpack-username">
                            {row.username || <span className="text-[var(--sc-text-mute)] italic">unknown</span>}
                          </span>
                        </td>
                        <td className="py-3 pr-3">
                          <span
                            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium"
                            style={{
                              background: `${accent}1a`,
                              color: accent,
                              border: `1px solid ${accent}44`,
                            }}
                            data-testid="active-subpack-tier"
                          >
                            {label}
                          </span>
                        </td>
                        <td className="py-3 pr-3 sc-tabular">
                          <span className="text-[var(--sc-text)] font-medium" data-testid="active-subpack-positions">
                            {row.positions_released}
                          </span>
                          <span className="text-[var(--sc-text-mute)]">
                            {' / '}
                            {row.total_positions}
                          </span>
                        </td>
                        <td className="py-3 pr-3">
                          <ProgressBar pct={row.progress_pct} accent={accent} />
                        </td>
                        <td className="py-3 pr-3 text-right sc-tabular text-[var(--sc-text-mute)] text-[12px]">{fmtRelative(row.created_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <AdminPagination page={currentPage} totalPages={totalPages} total={Number(data?.total ?? activeCount)} onChange={onPageChange} />
          </>
        )}
      </div>
    </div>
  );
};

export default ActiveSubpacksSubpage;
